/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Toolbar';

const ToolbarManager: ComponentManagerFn<'Toolbar'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Toolbar'>(platform, 'Toolbar'),
    type: 'Toolbar',
    name: 'Toolbar',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionButton',
          'ActiveUsers',
          'Alert',
          'Avatar',
          'AvatarList',
          'Badge',
          'Box',
          'Button',
          'ButtonDropdown',
          'ButtonGroup',
          'Checkbox',
          'Chip',
          'DarkModeToggle',
          'Form',
          'FullScreenToggle',
          'IOSSwitch',
          'IconButton',
          'LanguageSelect',
          'Link',
          'PageMenu',
          'ProgressIndicator',
          'SearchForm',
          'SwitchRole',
          'ToolbarDivider',
          'ToolbarSpacer',
          'Typography',
          'UserList',
          'UserPic',
          'UserProfileMenu',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    defaultChildren: ['ToolbarSpacer', 'Button', 'Button'],
    props: {
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    canHaveChildren: () => true,
    category: 'Toolbar',
    icon: { iconName: 'rectangle-wide' },
    description: `**Overview**<br/>
The Toolbar is a container component used to group actionable items horizontally. It is the standard building block for application headers, action bars, and footer controls. It manages the layout and alignment of buttons, search fields, and other utility controls.
Use this component to create the top navigation bar of your app or a row of action buttons above a data grid.

**1. Structure & Content**<br/>
The Toolbar is highly flexible and accepts a wide range of child components relevant to actions and navigation.
* **Action Elements:** Button, IconButton, ButtonDropdown, ButtonGroup, ActionButton.
* **Navigation & Layout:** ToolbarSpacer (pushes items to the right), ToolbarDivider, PageMenu, Link.
* **Inputs:** SearchForm, IOSSwitch, Checkbox, LanguageSelect.
* **Status & User:** Avatar, UserProfileMenu, ActiveUsers, Badge, Alert.

**2. Data Context**<br/>
* **Alias:** You can bind the Toolbar to a Data Store (alias). This sets the data context for all children within the toolbar. For example, if bound to a "Customer" store, the buttons inside can directly reference fields like $.Customer.name or $.Customer.status for labels or disable logic.

**3. Default Layout**<br/>
* **Initialization:** When created, it defaults to containing ['ToolbarSpacer', 'Button', 'Button']. This pattern (spacer first) typically pushes the buttons to the far right, which is a standard convention for page-level action bars.

**4. Best Use Cases**<br/>
* **Page Header:** Combining PageMenu, Typography (Title), ToolbarSpacer, and UserProfileMenu.
* **Grid Actions:** A bar above a table with SearchForm on the left and Button (Add New) on the right.
* **Footer:** A toolbar at the bottom of a form with Button (Save) and Button (Cancel).`,
  };
};

export default ToolbarManager;
